import { onMounted, ref, reactive, type Ref } from "vue";
import type { SummaryTypeRadioItem, ShopSelectValue, ShopListItem } from "../types";
import { ShopListInput } from "@/generated/graphql";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import { getWordList } from "@/common/graphql/expression";
import { getShopList } from "@/common/graphql/shop";

interface UseSalesSummaryInitParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  shopListItems: Ref<ShopListItem[] | null | undefined>;
  bufShopListItems: Ref<ShopListItem[] | null | undefined>;
  shopSelectValuesFrom: Ref<ShopSelectValue[]>;
  shopSelectValuesTo: Ref<ShopSelectValue[]>;
  searchShopSelectValuesFrom: Ref<string[]>;
  searchShopSelectValuesTo: Ref<string[]>;
  shopFromAllCheckFlag: Ref<"none" | "all">;
  shopToAllCheckFlag: Ref<"none" | "all">;
  shopDisabledFlag: Ref<boolean>;
  summeryTypeValues: Ref<SummaryTypeRadioItem>;
  summaryTypeRadioItems: SummaryTypeRadioItem[];
  isOperatorGroupShop: boolean;
  initDisplayByShopAuth: (code: string, name: string) => void;
  onClickSummaryType: (index: string) => Promise<void>;
  onClickSearch: (initial?: boolean) => Promise<void>;
}

/**
 * 画面表示時に必要な初期化処理を提供する composable。
 *
 * @returns 初期化処理を実行する関数群と初期表示に必要な状態
 */
export const useSalesSummaryInit = (params: UseSalesSummaryInitParams) => {
  const {
    errdialog,
    errMessage,
    shopListItems,
    bufShopListItems,
    shopSelectValuesFrom,
    shopSelectValuesTo,
    searchShopSelectValuesFrom,
    searchShopSelectValuesTo,
    shopFromAllCheckFlag,
    shopToAllCheckFlag,
    shopDisabledFlag,
    summeryTypeValues,
    summaryTypeRadioItems,
    isOperatorGroupShop,
    initDisplayByShopAuth,
    onClickSummaryType,
    onClickSearch,
  } = params;

  /**
   * 店舗一覧 input
   */
  const shopListInput: ShopListInput = reactive({
    chain_code: localStorageGetChainCode(),
    customer_code: localStorageGetCustomerCode(),
    limit: 10000,
    offset: 0,
  });

  const wordList = ref<Record<string, string>>({});

  ////////////////////////////////////////////////////////////////////////////////
  // 初期画面
  ////////////////////////////////////////////////////////////////////////////////
  onMounted(async (): Promise<void> => {
    try {
      //文言データを取得する
      wordList.value = await getWordList(
        localStorageGetChainCode(),
        "ListSalesSummary",
        localStorageGetCustomerCode()
      );
      searchShopSelectValuesFrom.value = [];
      searchShopSelectValuesTo.value = [];
      shopListItems.value = [];

      //店舗情報を取得する
      bufShopListItems.value = await getShopList(shopListInput);

      //店舗権限の場合は自身の店舗を配送元のデフォルトにする
      if (bufShopListItems.value && bufShopListItems.value.length > 0) {
        bufShopListItems.value?.forEach((shop_value) => {
          shopListItems.value?.push({
            shop_code: shop_value!.shop_code,
            name: shop_value!.name,
          });

          if (isOperatorGroupShop) {
            if (shop_value && shop_value.shop_code == localStorageGetShopCode()) {
              initDisplayByShopAuth(shop_value!.shop_code, shop_value!.name);
            }
          } else {
            shopSelectValuesFrom.value.push({
              shop_code: shop_value!.shop_code,
              name: shop_value!.name,
            });
            searchShopSelectValuesFrom.value.push(shop_value!.shop_code);
            shopFromAllCheckFlag.value = "all";
          }
          searchShopSelectValuesTo.value.push(shop_value!.shop_code);
          shopSelectValuesTo.value.push({
            shop_code: shop_value!.shop_code,
            name: shop_value!.name,
          });
        });
      }
      shopToAllCheckFlag.value = "all";
      shopDisabledFlag.value = false;
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value =
        err instanceof Error ? err.message : "予期しないエラーが発生しました。";
      return;
    }
    if (summeryTypeValues.value.index === "0") {
      summeryTypeValues.value = summaryTypeRadioItems[0];
      onClickSummaryType(summeryTypeValues.value.index);
    }
    await onClickSearch();
  });

  return {
    wordList,
  };
};
